import type { RatePlan, Resource, ResourceBlock } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ComingSoonState,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  NoPermissionState,
  Notice,
  PageHeader,
  ResourceDot,
  Skeleton,
  Tab,
  TabPanel,
  Table,
  TableWrap,
  Tabs,
  TabsList,
  Td,
  Th,
  Tr,
  toast,
} from '@horevia/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  Ban,
  BedDouble,
  Coins,
  Images,
  Info,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan, useSession } from '@/features/auth/session';
import { BlockFormDrawer } from '@/features/resources/BlockFormDrawer';
import { RatePlanFormDrawer } from '@/features/resources/RatePlanFormDrawer';
import { ResourceFormDrawer } from '@/features/resources/ResourceFormDrawer';
import {
  amenitiesQueryOptions,
  archiveResource,
  deleteRatePlan,
  deleteResourceBlock,
  ratePlansQueryOptions,
  resourceBlocksQueryOptions,
  resourceKeys,
  resourceQueryOptions,
  resourceTypesQueryOptions,
  resourcesQueryOptions,
  updateResourceStatus,
} from '@/features/resources/queries';
import {
  OPERATIONAL_STATUSES,
  blockBadgeTone,
  statusBadgeTone,
  statusTone,
} from '@/features/resources/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDate, formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/_app/resources/$resourceId')({
  component: ResourceDetailPage,
});

function ResourceDetailPage() {
  const { resourceId } = Route.useParams();
  const { t, i18n } = useTranslation(['resources', 'maintenance', 'common', 'errors']);
  const navigate = useNavigate();
  const session = useSession();
  const queryClient = useQueryClient();

  const canRead = useCan('resource.read');
  const canUpdate = useCan('resource.update');
  const canArchive = useCan('resource.archive');
  const canReadRates = useCan('rateplan.read');
  const canManageRates = useCan('rateplan.manage');
  const canReadBlocks = useCan('availability.read');
  const canBlock = useCan('availability.block');

  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [rateDrawer, setRateDrawer] = useState<{ open: boolean; plan?: RatePlan }>({ open: false });
  const [rateToDelete, setRateToDelete] = useState<RatePlan | null>(null);
  const [blockDrawer, setBlockDrawer] = useState<{ open: boolean; block?: ResourceBlock }>({
    open: false,
  });
  const [blockToDelete, setBlockToDelete] = useState<ResourceBlock | null>(null);

  const query = useQuery({ ...resourceQueryOptions(resourceId), enabled: canRead });
  const typesQuery = useQuery({ ...resourceTypesQueryOptions(), enabled: canUpdate });
  const amenitiesQuery = useQuery({ ...amenitiesQueryOptions(true), enabled: canRead });
  const ratesQuery = useQuery({ ...ratePlansQueryOptions(resourceId), enabled: canReadRates });
  const blocksQuery = useInfiniteQuery({
    ...resourceBlocksQueryOptions({ resourceId }),
    enabled: canReadBlocks,
  });
  const allResourcesQuery = useInfiniteQuery({ ...resourcesQueryOptions({}), enabled: canUpdate });

  const statusMutation = useMutation({
    mutationFn: ({
      resource,
      status,
    }: {
      resource: Resource;
      status: Resource['operationalStatus'];
    }) => updateResourceStatus(resource.id, status, resource.version),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.all });
      toast.success(
        t('resources:toasts.statusChanged', {
          status: t(`resources:status.${updated.operationalStatus}`),
        }),
      );
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveResource(resourceId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.all });
      toast.success(t('resources:toasts.archived'));
      setArchiveOpen(false);
      await navigate({ to: '/resources' });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const deleteRateMutation = useMutation({
    mutationFn: (plan: RatePlan) => deleteRatePlan(plan.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.all });
      toast.success(t('resources:toasts.ratePlanDeleted'));
      setRateToDelete(null);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const deleteBlockMutation = useMutation({
    mutationFn: (block: ResourceBlock) => deleteResourceBlock(block.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.all });
      toast.success(t('resources:toasts.blockDeleted'));
      setBlockToDelete(null);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack">
          <Skeleton variant="title" />
          <Skeleton height={120} />
          <Skeleton height={320} />
        </div>
      </Container>
    );
  }

  if (query.isError) {
    return (
      <Container>
        <ErrorState
          title={t('resources:detail.notFoundTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const resource = query.data;
  const rates = ratesQuery.data ?? [];
  const blocks = blocksQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const amenityLabels = new Map((amenitiesQuery.data ?? []).map((item) => [item.code, item.label]));
  const parentName = resource.parentResourceId
    ? (allResourcesQuery.data?.pages
        .flatMap((page) => page.data)
        .find((item) => item.id === resource.parentResourceId)?.name ?? resource.parentResourceId)
    : null;

  return (
    <Container>
      <PageHeader
        title={resource.name}
        subtitle={[
          resource.typeName,
          resource.internalRef,
          t('resources:card.capacity', {
            standard: resource.capacityStandard,
            max: resource.capacityMax,
          }),
        ]
          .filter(Boolean)
          .join(' · ')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('resources:title'),
            link: <Link to="/resources">{t('resources:title')}</Link>,
          },
          { label: resource.name },
        ]}
        actions={
          <>
            <Badge tone={statusBadgeTone(resource.operationalStatus)} dot>
              {t(`resources:status.${resource.operationalStatus}`)}
            </Badge>
            {canUpdate ? (
              <Button variant="secondary" onClick={() => setEditOpen(true)}>
                <Pencil className="ico" aria-hidden="true" />
                {t('common:actions.edit')}
              </Button>
            ) : null}
            {canUpdate || canArchive ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton label={t('common:actions.openMenu')}>
                    <MoreHorizontal className="ico ico-16" aria-hidden="true" />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canUpdate ? (
                    <>
                      <DropdownMenuLabel>{t('resources:status.change')}</DropdownMenuLabel>
                      {OPERATIONAL_STATUSES.map((status) => (
                        <DropdownMenuItem
                          key={status}
                          disabled={status === resource.operationalStatus}
                          onSelect={() => statusMutation.mutate({ resource, status })}
                        >
                          <ResourceDot
                            tone={statusTone(status)}
                            label={t(`resources:status.${status}`)}
                          />
                          {t(`resources:status.${status}`)}
                        </DropdownMenuItem>
                      ))}
                    </>
                  ) : null}
                  {canArchive && resource.isActive ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem danger onSelect={() => setArchiveOpen(true)}>
                        <Archive className="ico" aria-hidden="true" />
                        {t('resources:actions.archive')}
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </>
        }
      />

      <Tabs defaultValue="information">
        <TabsList>
          <Tab value="information" icon={<Info className="ico ico-16" aria-hidden="true" />}>
            {t('resources:detail.tabs.information')}
          </Tab>
          <Tab
            value="rates"
            icon={<Coins className="ico ico-16" aria-hidden="true" />}
            count={rates.length}
          >
            {t('resources:detail.tabs.rates')}
          </Tab>
          <Tab
            value="blocks"
            icon={<Ban className="ico ico-16" aria-hidden="true" />}
            count={blocks.length}
          >
            {t('resources:detail.tabs.blocks')}
          </Tab>
          <Tab value="composition" icon={<Layers className="ico ico-16" aria-hidden="true" />}>
            {t('resources:detail.tabs.composition')}
          </Tab>
          <Tab value="photos" icon={<Images className="ico ico-16" aria-hidden="true" />}>
            {t('resources:detail.tabs.photos')}
          </Tab>
        </TabsList>

        {/* ---------------- Informations ---------------- */}
        <TabPanel value="information">
          <div className="grid grid-2">
            <Card>
              <CardHeader
                title={t('resources:detail.identity')}
                icon={<BedDouble className="ico ico-16" aria-hidden="true" />}
              />
              <CardBody>
                <dl className="dl">
                  <dt>{t('resources:fields.type')}</dt>
                  <dd>{resource.typeName}</dd>
                  <dt>{t('resources:fields.internalRef')}</dt>
                  <dd>{resource.internalRef ?? '—'}</dd>
                  <dt>{t('resources:fields.floor')}</dt>
                  <dd>{resource.floor === null ? '—' : resource.floor}</dd>
                  <dt>{t('resources:fields.capacityStandard')}</dt>
                  <dd>{resource.capacityStandard}</dd>
                  <dt>{t('resources:fields.capacityMax')}</dt>
                  <dd>{resource.capacityMax}</dd>
                  <dt>{t('resources:fields.bedrooms')}</dt>
                  <dd>{resource.bedrooms ?? '—'}</dd>
                  <dt>{t('resources:fields.bathrooms')}</dt>
                  <dd>{resource.bathrooms ?? '—'}</dd>
                  <dt>{t('resources:fields.surface')}</dt>
                  <dd>{resource.surfaceM2 ? `${resource.surfaceM2} m²` : '—'}</dd>
                  <dt>{t('resources:fields.minDuration')}</dt>
                  <dd>{resource.minDuration ?? '—'}</dd>
                  <dt>{t('resources:fields.maxDuration')}</dt>
                  <dd>{resource.maxDuration ?? '—'}</dd>
                </dl>
                {resource.description ? <p className="t-13 mt-4">{resource.description}</p> : null}
                {resource.internalNotes ? (
                  <Notice tone="warning" className="mt-4">
                    <span className="t-13">{resource.internalNotes}</span>
                  </Notice>
                ) : null}
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title={t('resources:detail.features')}
                icon={<Layers className="ico ico-16" aria-hidden="true" />}
                iconTone="blue"
              />
              <CardBody>
                {resource.amenities.length === 0 ? (
                  <p className="hint mb-0">{t('resources:detail.noAmenities')}</p>
                ) : (
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {resource.amenities.map((code) => (
                      <Badge key={code} tone="neutral">
                        {amenityLabels.get(code) ?? code}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="panel-title mt-4">{t('resources:detail.openingHours')}</div>
                {resource.openingHours ? (
                  <dl className="dl t-13">
                    {Object.entries(resource.openingHours).map(([day, slots]) => (
                      <div key={day} style={{ display: 'contents' }}>
                        <dt>{day}</dt>
                        <dd>
                          {(slots ?? []).length === 0
                            ? '—'
                            : (slots ?? []).map((slot) => slot.join(' → ')).join(', ')}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="hint mb-0">{t('resources:detail.noOpeningHours')}</p>
                )}
              </CardBody>
            </Card>
          </div>
        </TabPanel>

        {/* ---------------- Tarifs ---------------- */}
        <TabPanel value="rates">
          <Card>
            <CardHeader
              title={t('resources:rates.title')}
              icon={<Coins className="ico ico-16" aria-hidden="true" />}
              iconTone="green"
              actions={
                canManageRates ? (
                  <Button variant="primary" size="sm" onClick={() => setRateDrawer({ open: true })}>
                    <Plus className="ico ico-14" aria-hidden="true" />
                    {t('resources:rates.add')}
                  </Button>
                ) : null
              }
            />
            {!canReadRates ? (
              <CardBody>
                <NoPermissionState title={t('common:states.noPermissionTitle')} />
              </CardBody>
            ) : rates.length === 0 ? (
              <EmptyState
                icon={<Coins className="ico ico-24" aria-hidden="true" />}
                title={t('resources:rates.empty')}
                description={t('resources:rates.emptyBody')}
              />
            ) : (
              <TableWrap>
                <Table compact>
                  <thead>
                    <tr>
                      <Th>{t('resources:rates.columns.name')}</Th>
                      <Th>{t('resources:rates.columns.unit')}</Th>
                      <Th align="right">{t('resources:rates.columns.price')}</Th>
                      <Th>{t('resources:rates.columns.validity')}</Th>
                      <Th>{t('resources:rates.columns.segment')}</Th>
                      <Th>{t('resources:rates.columns.status')}</Th>
                      <Th align="right">
                        <span className="sr-only">{t('maintenance:columns.actions')}</span>
                      </Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((plan) => (
                      <Tr key={plan.id}>
                        <Td strong>{plan.name}</Td>
                        <Td className="t-13">{t(`resources:rates.units.${plan.pricingUnit}`)}</Td>
                        <Td align="right" numeric>
                          {formatAmount(plan.price, i18n.language, plan.currencyCode)}
                        </Td>
                        <Td className="t-13">
                          {plan.validFrom || plan.validTo
                            ? `${plan.validFrom ? formatDate(`${plan.validFrom}T00:00:00Z`, i18n.language) : '…'} → ${plan.validTo ? formatDate(`${plan.validTo}T00:00:00Z`, i18n.language) : '…'}`
                            : t('resources:rates.always')}
                        </Td>
                        <Td className="t-13">
                          {t(`resources:rates.segments.${plan.customerSegment}`)}
                        </Td>
                        <Td>
                          {plan.isDefault ? (
                            <Badge tone="violet">{t('resources:rates.default')}</Badge>
                          ) : !plan.isActive ? (
                            <Badge tone="neutral">{t('resources:rates.inactive')}</Badge>
                          ) : (
                            <Badge tone="green" dot>
                              {t('resources:filters.active')}
                            </Badge>
                          )}
                        </Td>
                        <Td align="right">
                          {canManageRates ? (
                            <span className="row-actions">
                              <IconButton
                                label={`${t('common:actions.edit')} — ${plan.name}`}
                                onClick={() => setRateDrawer({ open: true, plan })}
                              >
                                <Pencil className="ico ico-16" aria-hidden="true" />
                              </IconButton>
                              <IconButton
                                label={`${t('common:actions.delete')} — ${plan.name}`}
                                onClick={() => setRateToDelete(plan)}
                              >
                                <Trash2 className="ico ico-16" aria-hidden="true" />
                              </IconButton>
                            </span>
                          ) : null}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Card>
        </TabPanel>

        {/* ---------------- Blocages ---------------- */}
        <TabPanel value="blocks">
          <Card>
            <CardHeader
              title={t('maintenance:title')}
              icon={<Ban className="ico ico-16" aria-hidden="true" />}
              iconTone="red"
              actions={
                canBlock ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setBlockDrawer({ open: true })}
                  >
                    <Plus className="ico ico-14" aria-hidden="true" />
                    {t('maintenance:create')}
                  </Button>
                ) : null
              }
            />
            {blocks.length === 0 ? (
              <EmptyState
                icon={<Ban className="ico ico-24" aria-hidden="true" />}
                title={t('maintenance:emptyTitle')}
                description={t('maintenance:emptyBody')}
              />
            ) : (
              <TableWrap>
                <Table compact>
                  <thead>
                    <tr>
                      <Th>{t('maintenance:columns.kind')}</Th>
                      <Th>{t('maintenance:columns.period')}</Th>
                      <Th>{t('maintenance:columns.reason')}</Th>
                      <Th>{t('maintenance:columns.createdBy')}</Th>
                      <Th align="right">
                        <span className="sr-only">{t('maintenance:columns.actions')}</span>
                      </Th>
                    </tr>
                  </thead>
                  <tbody>
                    {blocks.map((block) => (
                      <Tr key={block.id}>
                        <Td>
                          <Badge tone={blockBadgeTone(block.kind)}>
                            {t(`maintenance:kinds.${block.kind}`)}
                          </Badge>
                        </Td>
                        <Td className="t-13">
                          {formatDateTime(block.startsAt, i18n.language)} →{' '}
                          {formatDateTime(block.endsAt, i18n.language)}
                        </Td>
                        <Td className="t-13">{block.reason ?? '—'}</Td>
                        <Td className="t-13">{block.createdByName ?? '—'}</Td>
                        <Td align="right">
                          {canBlock && block.kind !== 'EXTERNAL' ? (
                            <span className="row-actions">
                              <IconButton
                                label={t('common:actions.edit')}
                                onClick={() => setBlockDrawer({ open: true, block })}
                              >
                                <Pencil className="ico ico-16" aria-hidden="true" />
                              </IconButton>
                              <IconButton
                                label={t('common:actions.delete')}
                                onClick={() => setBlockToDelete(block)}
                              >
                                <Trash2 className="ico ico-16" aria-hidden="true" />
                              </IconButton>
                            </span>
                          ) : null}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Card>
        </TabPanel>

        {/* ---------------- Composition (ADR-013) ---------------- */}
        <TabPanel value="composition">
          <div className="stack">
            <Notice tone="violet" title={t('resources:composition.ruleTitle')}>
              <p className="mb-0 t-13">{t('resources:composition.ruleBody')}</p>
            </Notice>

            <div className="grid grid-2">
              <Card>
                <CardHeader
                  title={t('resources:composition.units')}
                  icon={<Layers className="ico ico-16" aria-hidden="true" />}
                />
                <TableWrap>
                  <Table compact>
                    <thead>
                      <tr>
                        <Th>{t('resources:composition.unitCode')}</Th>
                        <Th>{t('resources:composition.unitName')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {resource.units.map((inventoryUnit) => (
                        <Tr key={inventoryUnit.id}>
                          <Td strong>{inventoryUnit.code}</Td>
                          <Td className="t-13">{inventoryUnit.name}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </Card>

              <Card>
                <CardHeader
                  title={t('resources:composition.children')}
                  icon={<BedDouble className="ico ico-16" aria-hidden="true" />}
                  iconTone="blue"
                />
                <CardBody>
                  {parentName ? (
                    <p className="t-13">
                      {t('resources:composition.parentOf', { name: parentName })}
                    </p>
                  ) : null}
                  {resource.children.length === 0 ? (
                    <p className="hint mb-0">{t('resources:composition.noChildren')}</p>
                  ) : (
                    <div className="stack stack--sm">
                      {resource.children.map((child) => (
                        <div key={child.id} className="row row--between">
                          <Link to="/resources/$resourceId" params={{ resourceId: child.id }}>
                            {child.name}
                          </Link>
                          <span className="t-12 muted">{child.internalRef ?? child.typeCode}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </TabPanel>

        {/* ---------------- Photos ---------------- */}
        <TabPanel value="photos">
          <Card>
            <ComingSoonState
              icon={<Images className="ico ico-32" aria-hidden="true" />}
              title={t('resources:detail.photosSoon')}
              description={t('resources:detail.photosSoonBody')}
            />
          </Card>
        </TabPanel>
      </Tabs>

      <ResourceFormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        propertyId={session.user.properties[0]?.id ?? resource.propertyId}
        types={typesQuery.data ?? []}
        amenities={amenitiesQuery.data ?? []}
        parentCandidates={allResourcesQuery.data?.pages.flatMap((page) => page.data) ?? []}
        resource={resource}
      />

      <RatePlanFormDrawer
        open={rateDrawer.open}
        onOpenChange={(open) => setRateDrawer((current) => ({ ...current, open }))}
        resourceId={resource.id}
        suggestedUnit={resource.defaultRate?.pricingUnit ?? 'NIGHT'}
        plan={rateDrawer.plan}
      />

      <BlockFormDrawer
        open={blockDrawer.open}
        onOpenChange={(open) => setBlockDrawer((current) => ({ ...current, open }))}
        resources={[resource]}
        block={blockDrawer.block}
        preset={{ resourceId: resource.id }}
      />

      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent
          title={t('resources:actions.archiveTitle', { name: resource.name })}
          description={t('resources:actions.archiveBody')}
          icon={<Archive className="ico ico-16" aria-hidden="true" />}
          iconTone="amber"
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">{t('common:actions.cancel')}</Button>
              </DialogClose>
              <Button
                variant="danger"
                loading={archiveMutation.isPending}
                onClick={() => archiveMutation.mutate()}
              >
                {t('resources:actions.archiveConfirm')}
              </Button>
            </>
          }
        />
      </Dialog>

      <Dialog open={rateToDelete !== null} onOpenChange={(open) => !open && setRateToDelete(null)}>
        {rateToDelete ? (
          <DialogContent
            title={t('resources:rates.deleteTitle', { name: rateToDelete.name })}
            description={t('resources:rates.deleteBody')}
            icon={<Trash2 className="ico ico-16" aria-hidden="true" />}
            iconTone="red"
            closeLabel={t('common:actions.close')}
            footer={
              <>
                <DialogClose asChild>
                  <Button variant="ghost">{t('common:actions.cancel')}</Button>
                </DialogClose>
                <Button
                  variant="danger"
                  loading={deleteRateMutation.isPending}
                  onClick={() => deleteRateMutation.mutate(rateToDelete)}
                >
                  {t('common:actions.delete')}
                </Button>
              </>
            }
          />
        ) : null}
      </Dialog>

      <Dialog
        open={blockToDelete !== null}
        onOpenChange={(open) => !open && setBlockToDelete(null)}
      >
        {blockToDelete ? (
          <DialogContent
            title={t('maintenance:delete.title')}
            description={t('maintenance:delete.body')}
            icon={<Trash2 className="ico ico-16" aria-hidden="true" />}
            iconTone="red"
            closeLabel={t('common:actions.close')}
            footer={
              <>
                <DialogClose asChild>
                  <Button variant="ghost">{t('common:actions.cancel')}</Button>
                </DialogClose>
                <Button
                  variant="danger"
                  loading={deleteBlockMutation.isPending}
                  onClick={() => deleteBlockMutation.mutate(blockToDelete)}
                >
                  {t('maintenance:delete.confirm')}
                </Button>
              </>
            }
          />
        ) : null}
      </Dialog>
    </Container>
  );
}
